/**
 * Dil motoru: nedensellik dili ve aşırı iddia heuristikleri.
 *
 * Bu motor "iddia" tespit eder, "hata" değil. Karar, çalışma desenine bağlanır:
 * deneysel desende nedensel dil meşrudur; ilişkisel/kesitsel desende aynı dil
 * aşırı iddia olur. Desen tespit edilemezse güven düşer ve karar verilmez.
 */

import type {
	Confidence,
	Issue,
	LanguageHit,
	LanguageResult,
	StudyDesign
} from '../types';
import type { DocumentIndex } from '../text/document';
import { excerpt } from '../text/normalize';

export interface LanguageEngineOutput {
	result: LanguageResult;
	issues: Issue[];
}

interface PatternSpec {
	re: RegExp;
	/** Eşleşme nedensel mi? */
	causal?: boolean;
	/** Eşleşmenin kendisi zaten çekinceli mi ("ilişkili", "birlikte")? */
	hedged?: boolean;
	label: string;
}

/** Nedensellik iddiası taşıyan ifadeler. */
const CAUSAL_PATTERNS: PatternSpec[] = [
	{
		re: /(?<![\p{L}])neden(?:i|ine|ini)?\s+ol(?:ur|du|muştur|maktadır|abilir|maktadır)?/giu,
		causal: true,
		label: 'neden oldu'
	},
	{
		re: /(?<![\p{L}])yol\s+aç(?:tı|mıştır|maktadır|abilir|tığı|tığını)/giu,
		causal: true,
		label: 'yol açtı'
	},
	{
		re: /(?<![\p{L}])etkile(?:di|miştir|mektedir|yebilir|diği|diğini|r)/giu,
		causal: true,
		label: 'etkiledi'
	},
	{
		re: /(?<![\p{L}])belirle(?:di|miştir|mektedir|yici|yicisi|diği)/giu,
		causal: true,
		label: 'belirledi'
	},
	{
		re: /(?<![\p{L}])sağla(?:dı|mıştır|maktadır|yacaktır|dığı)/giu,
		causal: true,
		label: 'sağladı'
	},
	{
		re: /(?<![\p{L}])ortaya\s+koy(?:du|muştur|maktadır)/giu,
		causal: true,
		label: 'ortaya koydu'
	},
	{
		re: /(?<![\p{L}])(?:art(?:ırdı|ırmıştır|ırmaktadır)|azalt(?:tı|mıştır|maktadır))/giu,
		causal: true,
		label: 'artırdı/azalttı'
	},
	{
		re: /(?<![\p{L}])(?:iyileştir(?:di|miştir|mektedir)|boz(?:du|muştur))/giu,
		causal: true,
		label: 'iyileştirdi/bozdu'
	},
	{
		re: /(?<![\p{L}])(?:sonucunda|sonucu\s+olarak|bu\s+nedenle\s+ortaya\s+çıkan)/giu,
		causal: true,
		label: 'sonucunda'
	},
	{
		// İngilizce
		re: /\b(?:caused|leads?\s+to|led\s+to|results?\s+in|resulted\s+in|brings?\s+about|is\s+responsible\s+for)\b/giu,
		causal: true,
		label: 'caused/leads to'
	},
	{
		// Zayıf/çekinceli ifadeler — bunlar nedensellik iddiası değildir
		re: /(?<![\p{L}])(?:ilişkili(?:dir|ydi)?|bağlantılı(?:dır)?|birlikte\s+(?:değiş|görül|seyret)|eşlik\s+et|prediktörü|yatkınlık|riski\s+art|associated\s+with|correlated\s+with)/giu,
		hedged: true,
		label: 'ilişkili'
	}
];

const ABSOLUTIST_PATTERNS: PatternSpec[] = [
	{ re: /(?<![\p{L}])kan(?:ıtla|ıtlar|ıtlamakta|ıtlanmıştır|ıt\s+niteliğindedir)/giu, label: 'kanıtlar' },
	{ re: /(?<![\p{L}])(?:kesinlikle|hiç\s+kuşkusuz|şüphesiz|tartışmasız)/giu, label: 'kesinlikle' },
	{ re: /(?<![\p{L}])(?:her\s+zaman|her\s+durumda|istisnasız)/giu, label: 'her zaman' },
	{ re: /(?<![\p{L}])(?:ispatla|ispat\s+et|kanıtlanmıştır)/giu, label: 'ispat' },
	{ re: /\b(?:proves?|proven|undeniably|unquestionably|without\s+a\s+doubt|always)\b/giu, label: 'proves/always' },
	{
		re: /(?<![\p{L}])(?:literatürde\s+ilk|ilk\s+kez\s+(?:olarak\s+)?(?:göster|ortaya|kanıtla))/giu,
		label: 'literatürde ilk'
	}
];

const GENERALIZATION_PATTERNS: PatternSpec[] = [
	{ re: /(?<![\p{L}])(?:tüm|bütün)\s+(?:insanlar|bireyler|öğrenciler|kadınlar|erkekler|toplum)/giu, label: 'tüm insanlar' },
	{ re: /(?<![\p{L}])(?:genelle(?:nebilir|ştirilebilir)|tüm\s+evrene)/giu, label: 'genellenebilir' },
	{ re: /(?<![\p{L}])(?:tüm\s+evren|evrense)/giu, label: 'evren' },
	{ re: /\b(?:all\s+(?:people|individuals|students)|everyone|universally)\b/giu, label: 'all/everyone' }
];

const PASSIVE_PATTERNS: PatternSpec[] = [
	{ re: /(?<![\p{L}])[\p{L}]+(?:ıl|il|ul|ül)(?:dı|di|du|dü|mış|miş|muş|müş|maktadır|mektedir)/giu, label: 'edilgen çatı' }
];

const SIGNIFICANCE_WORDS = /(?<![\p{L}])(?:anlamlı|manidar|significant)/giu;

export function runLanguageEngine(index: DocumentIndex): LanguageEngineOutput {
	const text = index.text;
	const issues: Issue[] = [];
	const hits: LanguageHit[] = [];

	const { design, evidence: designEvidence } = detectDesign(text);

	/* 1) Nedensellik ------------------------------------------------------- */

	const causalHits = collect(text, CAUSAL_PATTERNS, (spec, m) => {
		const before = text.slice(Math.max(0, m.index - 25), m.index);
		// "ilişkili" gibi çekinceli ifadeler nedensel sayılmaz
		if (spec.hedged) return null;
		// "etki" isim formu: "X'in etkisi" genellikle tartışma dilidir; yine işaretle
		const hedgedContext = /\bilişki|\bcorrelat|\bassociation|\bbirlikte\b/i.test(before);
		return { pattern: 'causal' as const, hedgedContext };
	});

	for (const h of causalHits) {
		const justified = design === 'experimental';
		const conf: Confidence =
			design === 'experimental' ? 'high' : design === 'unknown' ? 'medium' : 'high';

		hits.push({
			id: `lang-c${hits.length + 1}`,
			pattern: 'causal',
			match: h.match,
			sentence: h.sentence,
			location: index.locationAt(h.start, h.end),
			confidence: conf,
			design,
			justified
		});

		if (!justified) {
			const location = index.locationAt(h.start, h.end);
			issues.push({
				id: `lang-causal-${issues.length}`,
				code: design === 'unknown' ? 'lang.causal_no_design' : 'lang.causal_overclaim',
				category: 'language',
				severity: design === 'unknown' ? 'info' : 'warning',
				title:
					design === 'unknown'
						? 'Nedensel ifade, çalışma deseni belirsiz'
						: 'Nedensel ifade ilişkisel desenle çelişiyor',
				detail:
					`"${h.match}" ifadesi nedensellik iddia ediyor. ` +
					(design === 'unknown'
						? 'Yöntem bölümünden çalışma deseni çıkarılamadı; desen deneysel değilse bu ifade aşırı iddia olur.'
						: `Çalışma ${designLabel(design)} olarak tespit edildi; bu desen nedensel çıkarımı desteklemez.`),
				quote: excerpt(h.sentence, 260),
				suggestion: 'Nedensel dili ilişkisel dile çevirin: "ilişkiliydi", "birlikte görüldü", "yatkınlık gösterdi".',
				location,
				confidence: conf,
				decisive: true,
				meta: { match: h.match, design }
			});
		}
	}

	/* 2) Aşırı iddia ------------------------------------------------------- */

	for (const h of collect(text, ABSOLUTIST_PATTERNS, () => ({ pattern: 'absolutist' as const }))) {
		hits.push({
			id: `lang-a${hits.length + 1}`,
			pattern: 'absolutist',
			match: h.match,
			sentence: h.sentence,
			location: index.locationAt(h.start, h.end),
			confidence: 'high',
			design
		});
		issues.push({
			id: `lang-abs-${issues.length}`,
			code: 'lang.absolutist',
			category: 'language',
			severity: 'warning',
			title: 'Aşırı iddia ifadesi',
			detail: `"${h.match}" ifadesi bulguların kapsamını olduğundan geniş gösterebilir.`,
			quote: excerpt(h.sentence, 260),
			suggestion: 'İfadeyi ölçülendirin: "bulgular ... olduğunu göstermektedir", "... ile tutarlıdır".',
			location: index.locationAt(h.start, h.end),
			confidence: 'high',
			decisive: true,
			meta: { match: h.match }
		});
	}

	for (const h of collect(text, GENERALIZATION_PATTERNS, () => ({ pattern: 'generalization' as const }))) {
		hits.push({
			id: `lang-g${hits.length + 1}`,
			pattern: 'generalization',
			match: h.match,
			sentence: h.sentence,
			location: index.locationAt(h.start, h.end),
			confidence: 'medium',
			design
		});
		issues.push({
			id: `lang-gen-${issues.length}`,
			code: 'lang.generalization',
			category: 'language',
			severity: 'info',
			title: 'Genelleme ifadesi',
			detail: `"${h.match}" ifadesi örneklemin ötesine genelleme yapıyor.`,
			quote: excerpt(h.sentence, 220),
			suggestion: 'Örneklem sınırlarını belirtin ("bu örneklemde", "bu grupta").',
			location: index.locationAt(h.start, h.end),
			confidence: 'medium',
			decisive: false,
			meta: { match: h.match }
		});
	}

	/* 3) Anlamlılık iddiası, test yok ------------------------------------- */

	const sigHits = collectSignificanceWithoutTest(text);
	for (const h of sigHits) {
		hits.push({
			id: `lang-s${hits.length + 1}`,
			pattern: 'significance',
			match: h.match,
			sentence: h.sentence,
			location: index.locationAt(h.start, h.end),
			confidence: 'medium',
			design
		});
		issues.push({
			id: `lang-sig-${issues.length}`,
			code: 'lang.stat_claim_without_test',
			category: 'language',
			severity: 'warning',
			title: 'Test edilmemiş anlamlılık ifadesi',
			detail: `"${h.match}" ifadesi geçiyor ancak cümlede test istatistiği veya p değeri yok.`,
			quote: excerpt(h.sentence, 260),
			suggestion: 'İlgili teste ait istatistiği ve p değerini aynı cümlede raporlayın.',
			location: index.locationAt(h.start, h.end),
			confidence: 'medium',
			decisive: true,
			meta: { match: h.match }
		});
	}

	/* 4) Edilgen çatı yoğunluğu ------------------------------------------- */

	const passiveHits = collect(text, PASSIVE_PATTERNS, () => null).slice(0, 400);
	const sentences = text.split(/(?<=[.!?])\s+/).filter((s) => s.trim().length > 20);
	const passiveRatio = sentences.length > 0 ? passiveHits.length / sentences.length : 0;
	if (passiveRatio > 0.55 && sentences.length > 12) {
		issues.push({
			id: `lang-passive-${issues.length}`,
			code: 'lang.passive_overuse',
			category: 'language',
			severity: 'info',
			title: 'Edilgen çatı yoğunluğu yüksek',
			detail: `Cümlelerin yaklaşık %${Math.round(passiveRatio * 100)}'inde edilgen çatı kullanılmış. Etken çatı, sorumluluğu ve eylemi netleştirir.`,
			suggestion: 'Önemli bulguları etken çatıyla yeniden yazmayı deneyin.',
			location: {},
			confidence: 'low',
			decisive: false,
			meta: { ratio: passiveRatio }
		});
	}

	/* Skor ---------------------------------------------------------------- */

	const unjustifiedCausal = hits.filter((h) => h.pattern === 'causal' && !h.justified).length;
	const absolutist = hits.filter((h) => h.pattern === 'absolutist').length;
	const generalization = hits.filter((h) => h.pattern === 'generalization').length;

	let score = 100;
	score -= unjustifiedCausal * (design === 'unknown' ? 5 : 9);
	score -= absolutist * 7;
	score -= generalization * 3;
	score -= sigHits.length * 5;
	if (design === 'unknown' && (unjustifiedCausal > 0 || sigHits.length > 0)) {
		score -= 5; // desen belirsizliği ayrıca düşürür
	}

	return {
		result: {
			hits,
			design,
			designEvidence,
			summary: {
				causal: hits.filter((h) => h.pattern === 'causal').length,
				unjustifiedCausal,
				absolutist,
				generalization,
				statClaimWithoutTest: sigHits.length
			},
			score: clamp(score)
		},
		issues
	};
}

/* -------------------------------------------------------------------------- */
/* Desen tespiti                                                               */
/* -------------------------------------------------------------------------- */

interface DesignResult {
	design: StudyDesign;
	evidence?: string;
}

export function detectDesign(text: string): DesignResult {
	// Yöntem bölümüne odaklan
	const methodIdx = findSectionIndex(text, /(?:^|\n)\s*(?:\d+[.)]?\s*)?(?:yöntem|method|materyal|yöntemler)/i);
	const scope = methodIdx >= 0 ? text.slice(methodIdx, methodIdx + 6000) : text.slice(0, 12000);

	const experimental = [
		/random(?:ize|ised|ized|izasyon|izasyonu)/i,
		/rastgele\s+(?:atama|dağıtım|seçim)/i,
		/deney\s+grubu/i,
		/kontrol\s+grubu/i,
		/(?:ön\s*test|son\s*test|öntest|sontest)/i,
		/(?:deney|deneysel)\s+(?:desen|çalışma|gruplu)/i,
		/\b(?:müdahale|müdehale|intervention)\b/i,
		/(?:2\s*[x×]\s*2|faktöriyel)\s+desen/i,
		/\b(?:placebo|plasebo)\b/i
	];
	const quasi = [
		/yarı\s*deneysel/i,
		/quasi[\s-]?experimental/i,
		/tek\s*grup(?:lu)?\s+(?:ön|son)/i,
		/zaman\s+serisi/i
	];
	const correlational = [
		/ilişkisel\s+(?:desen|araştırma|tarama)/i,
		/(?:kesitsel|tarama|betimsel)\s+(?:desen|çalışma|araştırma)/i,
		/korelasyon(?:el)?\s+(?:analiz|desen)/i,
		/\b(?:survey|anket)\b.{0,60}\b(?:design|desen)\b/i,
		/\bcorrelational\b/i,
		/\bcross[\s-]?sectional\b/i
	];
	const caseStudy = [/vaka\s+(?:çalışması|incelemesi)/i, /case\s+study/i, /nitel\s+(?:desen|araştırma)/i];

	const firstMatch = (patterns: RegExp[]): string | undefined => {
		for (const p of patterns) {
			const m = p.exec(scope);
			if (m) return m[0];
		}
		return undefined;
	};

	const expEv = firstMatch(experimental);
	const quasiEv = firstMatch(quasi);
	const corrEv = firstMatch(correlational);
	const caseEv = firstMatch(caseStudy);

	if (quasiEv) return { design: 'quasi', evidence: quasiEv };
	if (expEv && !corrEv) return { design: 'experimental', evidence: expEv };
	if (corrEv) return { design: 'correlational', evidence: corrEv };
	if (caseEv) return { design: 'case', evidence: caseEv };
	if (expEv) return { design: 'experimental', evidence: expEv };
	return { design: 'unknown' };
}

function designLabel(d: StudyDesign): string {
	switch (d) {
		case 'correlational':
			return 'ilişkisel/kesitsel';
		case 'quasi':
			return 'yarı deneysel';
		case 'case':
			return 'vaka çalışması';
		case 'experimental':
			return 'deneysel';
		default:
			return 'belirsiz';
	}
}

function findSectionIndex(text: string, re: RegExp): number {
	const m = re.exec(text);
	return m ? m.index : -1;
}

/* -------------------------------------------------------------------------- */
/* Yardımcılar                                                                 */
/* -------------------------------------------------------------------------- */

interface RawHit {
	match: string;
	start: number;
	end: number;
	sentence: string;
	sentenceStart: number;
}

function collect(
	text: string,
	patterns: PatternSpec[],
	filter: (spec: PatternSpec, m: RegExpExecArray) => { pattern: LanguageHit['pattern']; hedgedContext?: boolean } | null
): (RawHit & { pattern: LanguageHit['pattern'] })[] {
	const out: (RawHit & { pattern: LanguageHit['pattern'] })[] = [];
	const seen = new Set<string>();

	for (const spec of patterns) {
		const re = new RegExp(spec.re.source, spec.re.flags);
		let m: RegExpExecArray | null;
		while ((m = re.exec(text))) {
			const verdict = filter(spec, m);
			if (!verdict) continue;
			const start = m.index;
			const end = start + m[0].length;
			const s = sentenceAt(text, start, end);
			const key = `${verdict.pattern}:${s.text.toLowerCase()}`;
			if (seen.has(key)) continue;
			seen.add(key);
			out.push({
				match: m[0],
				start,
				end,
				sentence: s.text,
				sentenceStart: s.start,
				pattern: verdict.pattern
			});
		}
	}
	return out;
}

function collectSignificanceWithoutTest(text: string): RawHit[] {
	const out: RawHit[] = [];
	const re = new RegExp(SIGNIFICANCE_WORDS.source, 'giu');
	let m: RegExpExecArray | null;
	const seen = new Set<string>();
	while ((m = re.exec(text))) {
		const s = sentenceAt(text, m.index, m.index + m[0].length);
		// Cümlede test istatistiği veya p var mı?
		if (/(?<![\p{L}\p{N}_])(?:p|t|F|r|z|χ\s*²?)\s*(?:[<>=,;]|\()/u.test(s.text)) continue;
		if (/(?:istatistiksel|statistical)/iu.test(s.text)) continue;
		const key = s.text.toLowerCase();
		if (seen.has(key)) continue;
		seen.add(key);
		out.push({
			match: m[0],
			start: m.index,
			end: m.index + m[0].length,
			sentence: s.text,
			sentenceStart: s.start
		});
	}
	return out;
}

function sentenceAt(text: string, start: number, end: number): { text: string; start: number } {
	const from = Math.max(
		text.lastIndexOf('.', start),
		text.lastIndexOf('!', start),
		text.lastIndexOf('?', start),
		text.lastIndexOf('\n', start)
	);
	const sentenceStart = from + 1;
	let to = text.length;
	for (let i = end; i < text.length; i++) {
		if (text[i] === '.' || text[i] === '!' || text[i] === '?' || text[i] === '\n') {
			if (text[i] === '.' && (/\d/.test(text[i - 1] ?? '') || /\d/.test(text[i + 1] ?? ''))) continue;
			to = i + 1;
			break;
		}
	}
	return { text: text.slice(sentenceStart, to).replace(/\s+/g, ' ').trim(), start: sentenceStart };
}

function clamp(v: number): number {
	return Math.max(0, Math.min(100, Math.round(v)));
}
